/**
 * Franklin Pet Food — Free Gift With Purchase (GWP) embed
 * Evaluates cart via App Proxy and applies actions through Cart AJAX API.
 *
 * Guards exist because a previous page-load-on-every-view call to /gwp/evaluate
 * produced a large 500 spike (Cloudflare) that correlated with Google Merchant
 * Center "product page unavailable" disapprovals.
 */
(function () {
  'use strict';

  var GWP_EVALUATE_URL = '/apps/franklin-discount-functions/api/gwp/evaluate';
  var DEBOUNCE_MS = 350;
  var EVALUATE_TIMEOUT_MS = 5000;
  var MAX_CONSECUTIVE_FAILURES = 3;
  var CIRCUIT_COOLDOWN_MS = 5 * 60 * 1000;
  var FINGERPRINT_KEY = 'gwp_cart_fp';
  var FAIL_COUNT_KEY = 'gwp_fail_count';
  var CIRCUIT_UNTIL_KEY = 'gwp_circuit_until';

  var isApplyingGwp = false;
  var gwpRequestDepth = 0;
  var evaluateTimer = null;
  var lastFingerprint = null;
  var forceEvaluate = false;
  var debug = false;

  function log() {
    if (!debug) return;
    var args = ['[GWP]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function isPaused() {
    return window.__GWP_PAUSED === true;
  }

  function isCrawler() {
    if (navigator.webdriver) return true;
    var ua = (navigator.userAgent || '').toLowerCase();
    return /googlebot|adsbot|storebot|google-inspectiontool|googleother|bingbot|bingpreview|yandexbot|duckduckbot|baiduspider|facebookexternalhit|slurp|semrush|ahrefs|dotbot|mj12bot|bytespider/.test(
      ua
    );
  }

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function isCircuitOpen() {
    var until = parseInt(storageGet(CIRCUIT_UNTIL_KEY) || '0', 10);
    return Date.now() < until;
  }

  function recordEvaluateSuccess() {
    storageSet(FAIL_COUNT_KEY, '0');
  }

  function recordEvaluateFailure() {
    var count = parseInt(storageGet(FAIL_COUNT_KEY) || '0', 10) + 1;
    storageSet(FAIL_COUNT_KEY, String(count));
    if (count >= MAX_CONSECUTIVE_FAILURES) {
      storageSet(CIRCUIT_UNTIL_KEY, String(Date.now() + CIRCUIT_COOLDOWN_MS));
      log('circuit open after', count, 'failures');
    }
  }

  function cartFingerprint(cart) {
    return (cart.items || [])
      .map(function (item) {
        return [
          item.variant_id,
          item.quantity,
          item.key,
          JSON.stringify(item.properties || {}),
        ].join(':');
      })
      .sort()
      .join('|');
  }

  function isInternalRequest() {
    return gwpRequestDepth > 0 || isApplyingGwp;
  }

  function mapCartItemToApiLine(item) {
    var sellingPlanId = null;
    if (
      item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id
    ) {
      sellingPlanId = item.selling_plan_allocation.selling_plan.id;
    }

    return {
      key: item.key,
      variant_id: item.variant_id,
      product_id: item.product_id,
      quantity: item.quantity,
      properties: item.properties || {},
      selling_plan_allocation: { selling_plan_id: sellingPlanId },
    };
  }

  function mapCartToApiPayload(cart) {
    var customer = window.__GWP_CUSTOMER__;
    return {
      cart: {
        lines: (cart.items || []).map(mapCartItemToApiLine),
      },
      customer: customer ? { id: customer.id, tags: customer.tags || [] } : null,
    };
  }

  function cartMutationUrl(url) {
    if (!url) return false;
    return /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
  }

  function gwpFetch(url, options) {
    gwpRequestDepth++;
    return fetch(url, options).finally(function () {
      gwpRequestDepth--;
    });
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (res) {
      if (!res.ok) throw new Error('cart.js failed: ' + res.status);
      return res.json();
    });
  }

  function evaluateCart(cart) {
    var payload = mapCartToApiPayload(cart);
    log('evaluate', payload);

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timeoutId = null;
    if (controller) {
      timeoutId = setTimeout(function () {
        controller.abort();
      }, EVALUATE_TIMEOUT_MS);
    }

    var options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    };
    if (controller) options.signal = controller.signal;

    return fetch(GWP_EVALUATE_URL, options)
      .then(function (res) {
        if (!res.ok) throw new Error('evaluate failed: ' + res.status);
        return res.json();
      })
      .finally(function () {
        if (timeoutId) clearTimeout(timeoutId);
      });
  }

  function applyAdd(action) {
    return gwpFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: action.variantId,
            quantity: action.quantity,
            properties: action.attributes || {},
          },
        ],
      }),
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed: ' + res.status);
      return res.json();
    });
  }

  function applyChange(payload) {
    return gwpFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('change failed: ' + res.status);
      return res.json();
    });
  }

  function applyAction(action, cart) {
    switch (action.type) {
      case 'add':
        return applyAdd(action);

      case 'remove':
        return applyChange({ id: action.lineKey, quantity: 0 });

      case 'update':
        return applyChange({ id: action.lineKey, quantity: action.quantity });

      case 'tagProperties': {
        var line = (cart.items || []).find(function (item) {
          return item.key === action.lineKey;
        });
        var qty = line ? line.quantity : 1;
        return applyChange({
          id: action.lineKey,
          quantity: qty,
          properties: action.attributes || {},
        });
      }

      default:
        log('unknown action type', action.type);
        return Promise.resolve();
    }
  }

  function applyActions(actions, cart) {
    var chain = Promise.resolve(cart);

    actions.forEach(function (action) {
      chain = chain.then(function (currentCart) {
        return applyAction(action, currentCart).then(function () {
          return fetchCart();
        });
      });
    });

    return chain;
  }

  function refreshCartUI(cart) {
    if (window.CartJS && cart) {
      window.CartJS.cart = cart;
    }

    if (window.Minicart && typeof window.Minicart.populateCartDrawer === 'function') {
      try {
        window.Minicart.populateCartDrawer();
      } catch (e) {
        log('Minicart refresh error', e);
      }
    }

    // Use cart.ready (constructCart with open=false) — cart.requestDone opens the drawer.
    if (typeof jQuery !== 'undefined') {
      if (cart) {
        jQuery(document).trigger('cart.ready', [cart]);
      } else {
        jQuery.getJSON('/cart', function (freshCart) {
          if (window.CartJS) window.CartJS.cart = freshCart;
          jQuery(document).trigger('cart.ready', [freshCart]);
        });
      }
    }

    document.dispatchEvent(new CustomEvent('gwp:cart-updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function markGiftLines() {
    document.querySelectorAll('[data-cart-item]').forEach(function (el) {
      var key = el.getAttribute('data-item-key');
      if (!key) {
        var keyEl = el.querySelector('[data-item-key]');
        key = keyEl ? keyEl.getAttribute('data-item-key') : null;
      }
      if (!key || !window.CartJS || !window.CartJS.cart) return;

      var item = (window.CartJS.cart.items || []).find(function (i) {
        return i.key === key;
      });
      if (!item || !item.properties) return;

      var isGift =
        item.properties._free_gift === 'true' ||
        item.properties._free_gift === true ||
        !!item.properties.__free_gift ||
        !!item.properties._giveaway;

      if (isGift) {
        el.classList.add('cart-item--gwp-gift');
        el.setAttribute('data-gwp-gift', 'true');
        if (!el.getAttribute('data-item-key')) {
          el.setAttribute('data-item-key', key);
        }
      } else {
        el.classList.remove('cart-item--gwp-gift');
        el.removeAttribute('data-gwp-gift');
      }
    });
  }

  function syncGwp() {
    if (isPaused() || isApplyingGwp || isCrawler()) return Promise.resolve();
    if (isCircuitOpen()) {
      log('skip: circuit open');
      return Promise.resolve();
    }

    isApplyingGwp = true;

    return fetchCart()
      .then(function (cart) {
        var items = cart.items || [];
        if (!items.length) {
          lastFingerprint = '';
          storageSet(FINGERPRINT_KEY, '');
          log('skip empty cart');
          return { cart: cart, applied: false };
        }

        var fingerprint = cartFingerprint(cart);
        var storedFingerprint = lastFingerprint != null ? lastFingerprint : storageGet(FINGERPRINT_KEY);
        if (!forceEvaluate && fingerprint === storedFingerprint) {
          lastFingerprint = fingerprint;
          log('skip unchanged cart');
          return { cart: cart, applied: false };
        }
        forceEvaluate = false;

        return evaluateCart(cart).then(function (result) {
          recordEvaluateSuccess();
          lastFingerprint = fingerprint;
          storageSet(FINGERPRINT_KEY, fingerprint);

          var actions = (result && result.actions) || [];
          if (!actions.length) {
            log('no actions');
            return { cart: cart, applied: false };
          }

          log('applying actions', actions);
          return applyActions(actions, cart).then(function (updatedCart) {
            lastFingerprint = cartFingerprint(updatedCart);
            storageSet(FINGERPRINT_KEY, lastFingerprint);
            return { cart: updatedCart, applied: true };
          });
        });
      })
      .then(function (result) {
        if (!result || !result.applied) return result;
        refreshCartUI(result.cart);
        markGiftLines();
        return result;
      })
      .catch(function (err) {
        recordEvaluateFailure();
        log('error', err);
      })
      .finally(function () {
        isApplyingGwp = false;
      });
  }

  function scheduleSync() {
    if (isPaused() || isInternalRequest() || isCrawler()) return;
    clearTimeout(evaluateTimer);
    evaluateTimer = setTimeout(function () {
      syncGwp();
    }, DEBOUNCE_MS);
  }

  function patchFetch() {
    if (!window.fetch || window.fetch.__gwpPatched) return;
    var originalFetch = window.fetch;

    window.fetch = function () {
      var args = arguments;
      var url =
        typeof args[0] === 'string'
          ? args[0]
          : args[0] instanceof Request
            ? args[0].url
            : '';

      var promise = originalFetch.apply(this, args);

      if (cartMutationUrl(url) && !isInternalRequest()) {
        promise.then(function () {
          forceEvaluate = true;
          scheduleSync();
        }).catch(function () {});
      }

      return promise;
    };

    window.fetch.__gwpPatched = true;
  }

  function patchXHR() {
    if (XMLHttpRequest.prototype.__gwpPatched) return;

    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__gwpUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      var url = xhr.__gwpUrl;

      if (cartMutationUrl(url) && !isInternalRequest()) {
        xhr.addEventListener('loadend', function () {
          forceEvaluate = true;
          scheduleSync();
        });
      }

      return originalSend.apply(this, arguments);
    };

    XMLHttpRequest.prototype.__gwpPatched = true;
  }

  function bindCartEvents() {
    document.addEventListener('cart:updated', scheduleSync);

    if (window.CartJS && typeof window.CartJS.on === 'function') {
      ['cart.requestComplete', 'item.added', 'cart.updated', 'item.removed'].forEach(
        function (eventName) {
          try {
            window.CartJS.on(eventName, scheduleSync);
          } catch (e) {}
        }
      );
    }

    if (typeof jQuery !== 'undefined') {
      jQuery(document).on('cart.requestDone cart.requestComplete', function () {
        scheduleSync();
      });
    }
  }

  function init() {
    debug =
      window.location.search.indexOf('gwp_debug=1') !== -1 ||
      window.__GWP_DEBUG === true;

    if (isCrawler()) {
      log('skip init for crawler');
      return;
    }

    patchFetch();
    patchXHR();
    bindCartEvents();

    window.__GWP__ = {
      sync: syncGwp,
      schedule: scheduleSync,
    };

    log('initialized');

    var initialCount = parseInt(window.__GWP_CART_COUNT__, 10);
    if (initialCount > 0) {
      scheduleSync();
    } else {
      lastFingerprint = '';
      storageSet(FINGERPRINT_KEY, '');
      log('skip initial evaluate: empty cart');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
